import {
  type AnyPerson,
  type AnyPersonCommon,
  createCodeableConcept,
  createExtensionValue,
  createFhirDate,
  createFullHumanName,
  ExtensionUrl,
  findExtensionValue,
  getAddressLines,
  getCity,
  getContactValues,
  getCountry,
  getEmail,
  getFirstCodeFromCodableConcepts,
  getIdentifier,
  getPersonNames,
  getPostalCode,
  type LanguagePreference,
  LanguagePreferenceDisplay,
  PersonIdentityCode,
  system,
  valsOrNone,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { Address, Extension } from 'fhir/r4';
import { createAddressList } from '../organization/address';
import {
  createEmailContactPoint,
  createPrimaryPhoneContactPoint,
  createSecondaryPhoneContactPoint,
  mergeContactPoints,
} from '../organization/contact-point';

import type {
  Coordinates,
  PersonContact,
  PersonContactWithAddressEmergencyAccess,
  PersonGeneral,
} from './schema';

interface ID {
  code: PersonIdentityCode;
  value: string;
}

const identifierCodes = Object.values(PersonIdentityCode);

const isValidID = (id: Partial<ID>): id is ID => !!(id.code && id.value);

export const getPersonIdentifier = (
  identifier?: fhir4.Identifier[],
): ID | undefined =>
  identifierCodes
    .map((code) => ({
      code,
      value: getIdentifier(code, identifier),
    }))
    .find(isValidID);

export const mapFhirPersonToGeneralModel = <T extends AnyPerson = AnyPerson>(
  person: T,
): Partial<PersonGeneral> => {
  const { gender = 'unknown', birthDate, identifier } = person;
  const { familyName, givenName, prefix } = getPersonNames(person);

  const id = getPersonIdentifier(identifier);

  return {
    gender,
    birthDate,
    givenName,
    familyName,
    prefixName: prefix,
    identifierValue: id?.value,
    identifierCode: id?.code,
  };
};

const getEmergencyAccess = findExtensionValue(
  ExtensionUrl.addressEmergencyAccess,
);
export const getAddressPlaceId = findExtensionValue(
  ExtensionUrl.addressGooglePlaceId,
);
export const getAddressLongitude = findExtensionValue(
  ExtensionUrl.addressLongitude,
);
export const getAddressLatitude = findExtensionValue(
  ExtensionUrl.addressLatitude,
);

export const mapFhirPersonToContactModel = <T extends AnyPerson = AnyPerson>(
  person: T,
): Partial<PersonContact | PersonContactWithAddressEmergencyAccess> => {
  const { telecom, address, communication } = person;

  const telecomEmail = getEmail(telecom);
  const telecomPhoneNumbers = getContactValues('phone', telecom);

  const addressCity = getCity(address);
  const addressPostalCode = getPostalCode(address);
  const addressLines = getAddressLines(address);
  const firstAddress = first<Address>(address);

  const addressCountry = getCountry(firstAddress);

  const addressEmergencyAccess = getEmergencyAccess(firstAddress?.extension);
  const addressPlaceId = getAddressPlaceId(firstAddress?.extension);
  const addressCoordinates = getCoordinates(firstAddress);

  if (person.resourceType === 'Patient') {
    const patientCommunication = communication as fhir4.PatientCommunication[];
    const languageCodableConcept = first(patientCommunication)?.language;
    const languagePreference = languageCodableConcept
      ? (getFirstCodeFromCodableConcepts([
          languageCodableConcept,
        ]) as LanguagePreference)
      : undefined;

    return {
      addressLines,
      addressCity,
      addressCountry,
      telecomEmail,
      telecomPhoneNumbers,
      addressPostalCode,
      addressEmergencyAccess,
      addressPlaceId,
      addressCoordinates,
      languagePreference,
    };
  }

  if (person.resourceType === 'RelatedPerson') {
    const patientCommunication =
      communication as fhir4.RelatedPersonCommunication[];
    const languageCodableConcept = first(patientCommunication)?.language;
    const languagePreference = languageCodableConcept
      ? (getFirstCodeFromCodableConcepts([
          languageCodableConcept,
        ]) as LanguagePreference)
      : undefined;

    return {
      addressLines,
      addressCity,
      addressCountry,
      telecomEmail,
      telecomPhoneNumbers,
      addressPostalCode,
      addressEmergencyAccess,
      addressPlaceId,
      addressCoordinates,
      languagePreference,
    };
  }

  return {
    addressLines,
    addressCity,
    addressCountry,
    telecomEmail,
    telecomPhoneNumbers,
    addressPostalCode,
    addressEmergencyAccess,
    addressPlaceId,
    addressCoordinates,
  };
};

type RequiredIdentifier = Required<Pick<fhir4.Identifier, 'type' | 'value'>>;

const createIdentifier = (
  identifierCode?: PersonIdentityCode,
  identifierValue?: string,
): RequiredIdentifier | undefined => {
  if (identifierCode && identifierValue) {
    return {
      type: createCodeableConcept(identifierCode),
      value: identifierValue,
    };
  }
  return undefined;
};

type FhirGeneralFields = 'birthDate' | 'gender' | 'identifier' | 'name';

type FhirGeneralPartial = Pick<AnyPersonCommon, FhirGeneralFields>;

export const mapPersonGeneralToFhirPerson = (
  person: Partial<PersonGeneral>,
): Partial<FhirGeneralPartial> => {
  const {
    gender,
    birthDate,
    familyName,
    givenName,
    prefixName,
    identifierCode,
    identifierValue,
  } = person;

  const name = createFullHumanName({
    given: givenName,
    family: familyName,
    prefix: prefixName,
    use: 'official',
  });

  const dateOfBirth = birthDate
    ? createFhirDate(new Date(birthDate))
    : undefined;

  const requiredIdentifier = createIdentifier(identifierCode, identifierValue);
  const identifier = compact(valsOrNone([requiredIdentifier]));

  return {
    birthDate: dateOfBirth,
    gender,
    name,
    identifier,
  };
};

export const mergePersonGeneralWithFhir = <T extends AnyPerson>(
  fhir: T,
  general: Partial<PersonGeneral>,
): T => {
  const fhirPartial = mapPersonGeneralToFhirPerson(general);

  // Always override general values
  return { ...fhir, ...fhirPartial };
};

type FhirContactFields = 'telecom' | 'address' | 'communication';

type FhirContactPartial = Pick<AnyPersonCommon, FhirContactFields>;

const createEmergencyAccessExtension = createExtensionValue(
  ExtensionUrl.addressEmergencyAccess,
);
export const createAddressPlaceId = createExtensionValue(
  ExtensionUrl.addressGooglePlaceId,
);
export const createAddressLatitude = createExtensionValue(
  ExtensionUrl.addressLatitude,
);
export const createAddressLongitude = createExtensionValue(
  ExtensionUrl.addressLongitude,
);

const isPatientContact = (
  contact?: Partial<PersonContact | PersonContactWithAddressEmergencyAccess>,
): contact is Partial<PersonContactWithAddressEmergencyAccess> =>
  !!(contact as PersonContactWithAddressEmergencyAccess)
    ?.addressEmergencyAccess;

export const createAddressExtensionList = (
  contact: Partial<
    PersonContact | PersonContactWithAddressEmergencyAccess
  > = {},
): Extension[] | undefined => {
  const { addressCoordinates, addressPlaceId } = contact;
  const addressEmergencyAccess =
    isPatientContact(contact) && contact.addressEmergencyAccess;
  const emergencyAccess =
    addressEmergencyAccess &&
    createEmergencyAccessExtension(addressEmergencyAccess);
  const placeId = addressPlaceId && createAddressPlaceId(addressPlaceId);
  const coordinatesLatitude =
    addressCoordinates && createAddressLatitude(addressCoordinates.latitude);
  const coordinatesLongitude =
    addressCoordinates && createAddressLongitude(addressCoordinates.longitude);

  const extensions = [
    emergencyAccess,
    placeId,
    coordinatesLatitude,
    coordinatesLongitude,
  ];

  return valsOrNone(compact(extensions));
};

export const mapPersonContactToFhirPartial = (
  contact: Partial<PersonContactWithAddressEmergencyAccess>,
): Partial<FhirContactPartial> => {
  const [primary, secondary] = contact.telecomPhoneNumbers || [];

  const telecom = mergeContactPoints([
    createEmailContactPoint(contact.telecomEmail),
    createPrimaryPhoneContactPoint(primary),
    createSecondaryPhoneContactPoint(secondary),
  ]);

  const extension: Extension[] | undefined =
    createAddressExtensionList(contact);

  const address: fhir4.Address = {
    line: contact.addressLines,
    postalCode: contact.addressPostalCode,
    city: contact.addressCity,
    country: contact.addressCountry,
    extension,
  };

  const addressList = createAddressList(address);

  // For some reason the resourceType is not referring to the correct type.
  return { address: addressList, telecom };
};

export const mapPatientContactToFhirPartial = (
  contact: Partial<PersonContactWithAddressEmergencyAccess>,
): Partial<fhir4.Patient> => {
  const personContact = mapPersonContactToFhirPartial(contact);

  const languagePreference = contact.languagePreference;

  const communication = languagePreference
    ? [
        {
          language: createCodeableConcept(
            languagePreference,
            LanguagePreferenceDisplay[languagePreference],
            LanguagePreferenceDisplay[languagePreference],
            system,
          ),
          preferred: true,
        },
      ]
    : undefined;

  return { ...personContact, communication };
};

export const mapRelatedPersonContactToFhirPartial = (
  contact: Partial<PersonContactWithAddressEmergencyAccess>,
): Partial<fhir4.RelatedPerson> => {
  const personContact = mapPersonContactToFhirPartial(contact);

  const languagePreference = contact.languagePreference;

  const communication = languagePreference
    ? [
        {
          language: createCodeableConcept(
            languagePreference,
            LanguagePreferenceDisplay[languagePreference],
            LanguagePreferenceDisplay[languagePreference],
            system,
          ),
          preferred: true,
        },
      ]
    : undefined;

  return { ...personContact, communication };
};

export const mergePersonContactWithFhir = <T extends AnyPerson>(
  fhir: T,
  contact: Partial<PersonContact>,
): T => {
  const fhirPartial = mapPersonContactToFhirPartial(contact);

  //Practitioners have a different communication schema in FHIR
  if (
    fhir.resourceType === 'Patient' ||
    fhir.resourceType === 'RelatedPerson'
  ) {
    const languagePreference = contact.languagePreference;

    const communication = languagePreference
      ? [
          {
            language: createCodeableConcept(
              languagePreference,
              LanguagePreferenceDisplay[languagePreference],
              LanguagePreferenceDisplay[languagePreference],
              system,
            ),
            preferred: true,
          },
        ]
      : undefined;

    fhirPartial.communication = communication;

    return { ...fhir, ...fhirPartial };
  }
  // Always override contact values
  return { ...fhir, ...fhirPartial };
};

export const getCoordinates = (firstAddress: Address | undefined) => {
  const addressLongitude = getAddressLongitude(firstAddress?.extension);
  const addressLatitude = getAddressLatitude(firstAddress?.extension);

  const hasCoordinates = addressLatitude && addressLongitude;

  const addressCoordinates: Coordinates | undefined = hasCoordinates
    ? {
        latitude: addressLatitude,
        longitude: addressLongitude,
      }
    : undefined;
  return addressCoordinates;
};
